/**
 * Oturum & Güvenlik Ayarları kartı (admin/mudur/baskan).
 *
 * Yöneticiye sunduğu ayarlar:
 *  - Access token süresi (dakika)
 *  - Refresh süresi: Beni Hatırla işaretliyken / işaretsizken
 *  - Idle (hareketsizlik) çıkışı aktif mi? Uyarı süresi (dk) + uyarı sonrası çıkış (sn)
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save, ShieldCheck } from "lucide-react";
import { BACKEND_URL } from "@/config";
import { buildApiCandidates, shouldFailoverBackendRequest } from "@/backendBases";

const API = BACKEND_URL;

const DEFAULTS = {
  access_token_minutes: 15,
  refresh_token_remember_me_minutes: 60 * 24 * 30,
  refresh_token_short_minutes: 60 * 8,
  idle_enabled: true,
  idle_warn_minutes: 30,
  idle_logout_seconds_after_warn: 120,
};

function minsToHumanShort(mins) {
  if (mins == null || Number.isNaN(mins)) return "—";
  if (mins < 60) return `${mins} dk`;
  if (mins < 60 * 24) return `${Math.round((mins / 60) * 10) / 10} sa`;
  return `${Math.round((mins / (60 * 24)) * 10) / 10} gün`;
}

async function _getWithFailover(path) {
  const candidates = buildApiCandidates(API);
  let lastErr = null;
  for (const base of candidates) {
    const url = base ? `${base}${path}` : path;
    try {
      const res = await axios.get(url, { withCredentials: true, timeout: 15000 });
      return res;
    } catch (err) {
      lastErr = err;
      if (shouldFailoverBackendRequest(err)) continue;
      throw err;
    }
  }
  throw lastErr;
}

async function _putWithFailover(path, body) {
  const candidates = buildApiCandidates(API);
  let lastErr = null;
  for (const base of candidates) {
    const url = base ? `${base}${path}` : path;
    try {
      const res = await axios.put(url, body, { withCredentials: true, timeout: 15000 });
      return res;
    } catch (err) {
      lastErr = err;
      if (shouldFailoverBackendRequest(err)) continue;
      throw err;
    }
  }
  throw lastErr;
}

export default function SecuritySettingsCard() {
  const [values, setValues] = useState(DEFAULTS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await _getWithFailover("/api/admin/security-settings");
      const data = res?.data || {};
      setValues({ ...DEFAULTS, ...data });
      setLoaded(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        toast.error("Bu ayarları görmek için yetkiniz yok.");
      } else {
        toast.error("Güvenlik ayarları alınamadı.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await _putWithFailover("/api/admin/security-settings", values);
      const data = res?.data || {};
      setValues({ ...DEFAULTS, ...data });
      toast.success("Güvenlik ayarları kaydedildi.");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403) {
        toast.error("Bu ayarları değiştirmek için yetkiniz yok.");
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Ayarlar kaydedilemedi.");
      }
    } finally {
      setSaving(false);
    }
  };

  const setNum = (key, raw, { min, max }) => {
    const n = Number.parseInt(String(raw).replace(/\D+/g, ""), 10);
    if (!Number.isFinite(n)) return;
    const clamped = Math.max(min, Math.min(max, n));
    setValues((v) => ({ ...v, [key]: clamped }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-green-700" />
          Oturum & Güvenlik Ayarları
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Kullanıcıların oturum süresi ve hareketsizlik (idle) davranışını buradan yönetin.
          Değişiklikler yeni girişlerde geçerli olur; mevcut oturumlar etkilenmez.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading && !loaded ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor...
          </div>
        ) : (
          <>
            <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-xs">Access Token Süresi (dakika)</Label>
                <Input
                  type="number"
                  min={5}
                  max={60}
                  value={values.access_token_minutes}
                  onChange={(e) => setNum("access_token_minutes", e.target.value, { min: 5, max: 60 })}
                  data-testid="sec-access-mins"
                />
                <p className="text-[11px] text-muted-foreground">
                  5–60 dk. Kısa tutmak güvenliği artırır; kullanıcı fark etmez (otomatik tazelenir).
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Refresh Süresi — "Beni Hatırla" işaretli (dk)</Label>
                <Input
                  type="number"
                  min={60}
                  max={60 * 24 * 90}
                  value={values.refresh_token_remember_me_minutes}
                  onChange={(e) =>
                    setNum("refresh_token_remember_me_minutes", e.target.value, {
                      min: 60,
                      max: 60 * 24 * 90,
                    })
                  }
                  data-testid="sec-refresh-remember"
                />
                <p className="text-[11px] text-muted-foreground">
                  1 saat – 90 gün. Önerilen: 30 gün ({minsToHumanShort(values.refresh_token_remember_me_minutes)}).
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Refresh Süresi — "Beni Hatırla" işaretsiz (dk)</Label>
                <Input
                  type="number"
                  min={30}
                  max={60 * 24}
                  value={values.refresh_token_short_minutes}
                  onChange={(e) =>
                    setNum("refresh_token_short_minutes", e.target.value, {
                      min: 30,
                      max: 60 * 24,
                    })
                  }
                  data-testid="sec-refresh-short"
                />
                <p className="text-[11px] text-muted-foreground">
                  30 dk – 24 saat. Önerilen: 8 saat ({minsToHumanShort(values.refresh_token_short_minutes)}).
                </p>
              </div>
            </section>

            <section className="border-t pt-4 space-y-4">
              <div className="flex items-center gap-3">
                <Switch
                  id="idle-enabled"
                  checked={Boolean(values.idle_enabled)}
                  onCheckedChange={(c) => setValues((v) => ({ ...v, idle_enabled: Boolean(c) }))}
                  data-testid="sec-idle-enabled"
                />
                <Label htmlFor="idle-enabled" className="cursor-pointer">
                  Hareketsizlik (idle) çıkışı aktif
                </Label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Idle Uyarı Süresi (dakika)</Label>
                  <Input
                    type="number"
                    min={5}
                    max={120}
                    value={values.idle_warn_minutes}
                    onChange={(e) => setNum("idle_warn_minutes", e.target.value, { min: 5, max: 120 })}
                    disabled={!values.idle_enabled}
                    data-testid="sec-idle-warn"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Bu süre boyunca aktivite olmazsa uyarı modalı çıkar (5–120 dk).
                  </p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Uyarı Sonrası Çıkış (saniye)</Label>
                  <Input
                    type="number"
                    min={30}
                    max={600}
                    value={values.idle_logout_seconds_after_warn}
                    onChange={(e) =>
                      setNum("idle_logout_seconds_after_warn", e.target.value, { min: 30, max: 600 })
                    }
                    disabled={!values.idle_enabled}
                    data-testid="sec-idle-grace"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Modal'da geri sayım süresi (30–600 sn). Kullanıcı "Devam Et" demezse otomatik çıkış.
                  </p>
                </div>
              </div>
            </section>

            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={saving || loading} data-testid="sec-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                <span className="ml-2">Kaydet</span>
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
